/**

 * Shared helpers for Pipeline Discovery UI (logs/metrics display and API fallbacks).

 */



export function parsePipelineIdFields(pipelineId) {

  if (!pipelineId) return { type: null, name: null, region: null, emrClusterId: null };

  const parts = String(pipelineId).split("_");

  if (parts.length < 3) {

    return { type: null, name: null, region: null, emrClusterId: null };

  }

  const type = parts[0];

  const region = parts[parts.length - 1];

  let name;

  let emrClusterId = null;

  if (type === "emr" && parts.length >= 4) {

    emrClusterId = parts[parts.length - 2];

    name = parts.slice(1, -2).join("_");

  } else {

    name = parts.slice(1, -1).join("_");

  }

  return { type, name, region, emrClusterId };

}



export function getPipelineFromDiscovery(discoveryResult, pipelineId) {

  if (!discoveryResult?.pipelines || !pipelineId) return null;

  const {

    glue_jobs = [],

    step_functions = [],

    emr_clusters = [],

    airflow_dags = [],

  } = discoveryResult.pipelines;

  for (const p of [

    ...glue_jobs,

    ...step_functions,

    ...emr_clusters,

    ...airflow_dags,

  ]) {

    if (p.pipeline_id === pipelineId || p.id === pipelineId) return p;

  }

  return null;

}



export function logsFromPipelineDiscovery(pipeline) {

  if (!pipeline?.log_paths) return { cloudwatch: [], s3: [] };

  const cloudwatch = [];

  const s3 = [];

  for (const path of pipeline.log_paths.cloudwatch || []) {

    const pathStr = typeof path === "object" && path.path != null ? path.path : path;

    if (!pathStr) continue;

    cloudwatch.push({

      path: pathStr,

      onboarded: typeof path === "object" ? Boolean(path.onboarded) : true,

    });

  }

  for (const path of pipeline.log_paths.s3 || []) {

    const pathStr = typeof path === "object" && path.path != null ? path.path : path;

    if (!pathStr) continue;

    s3.push({

      path: pathStr,

      onboarded: typeof path === "object" ? Boolean(path.onboarded) : true,

    });

  }

  return { cloudwatch, s3 };

}



/** Per-pipeline log_paths from discover response (matches GET /discover pipelines[].log_paths). */

export function resolvePipelineLogPaths(pipeline, discoveryResults) {

  const pipelineId = pipeline?.pipeline_id || pipeline?.id;

  const discP =

    discoveryResults && pipelineId

      ? getPipelineFromDiscovery(discoveryResults, pipelineId)

      : null;

  return logsFromPipelineDiscovery(discP || pipeline);

}



export function apiLogItemsToPathGroups(logItems = []) {

  const cloudwatch = [];

  const s3 = [];

  for (const item of logItems) {

    const path =

      item.path ||

      item.log_arn ||

      item.log_group_arn ||

      item.log_group ||

      item.s3_uri;

    if (!path) continue;

    const entry = {

      path,

      onboarded: Boolean(item.onboarded),

    };

    if (

      typeof path === "string" &&

      (path.includes("arn:aws:logs") || path.includes("log-group"))

    ) {

      cloudwatch.push(entry);

    } else {

      s3.push(entry);

    }

  }

  return { cloudwatch, s3 };

}



/** Merge onboarded flags from GET /logs onto discover-shaped path groups. */

export function mergeLogPathsWithApi(discoveryPaths, apiLogItems = []) {

  const apiGroups = apiLogItemsToPathGroups(apiLogItems);

  const onboardedByPath = new Map();

  for (const entry of [...apiGroups.cloudwatch, ...apiGroups.s3]) {

    onboardedByPath.set(entry.path, entry.onboarded);

  }

  const apply = (list) =>

    (list || []).map((item) => {

      const path = typeof item === "object" ? item.path : item;

      if (path && onboardedByPath.has(path)) {

        return { ...item, path, onboarded: onboardedByPath.get(path) };

      }

      return typeof item === "object"

        ? item

        : { path: item, onboarded: false };

    });

  return {

    cloudwatch: apply(discoveryPaths.cloudwatch),

    s3: apply(discoveryPaths.s3),

  };

}



/** Metrics from discover snapshot only (no hardcoded catalog). */

export function resolvePipelineMetrics(pipeline, discoveryResults) {

  if ((pipeline?.metrics || []).length > 0) return pipeline.metrics;

  if (discoveryResults?.pipelines) {

    const discP = getPipelineFromDiscovery(

      discoveryResults,

      pipeline.pipeline_id || pipeline.id,

    );

    if (discP?.metrics?.length) return discP.metrics;

  }

  if (discoveryResults?.metrics?.length) {

    const filtered = discoveryResults.metrics.filter(

      (m) =>

        m.pipeline_name === pipeline.name &&

        (!m.pipeline_type ||

          m.pipeline_type === (pipeline.type || pipeline.pipeline_type)),

    );

    if (filtered.length) return filtered;

  }

  return [];

}



export function metricSelectionKey(metric) {
  const ns = metric?.namespace || "";
  const name = metric?.name || "";
  const pipelineName = metric?.pipeline_name || "";
  const pipelineType = metric?.pipeline_type || "";
  return `${ns}:${name}:${pipelineName}:${pipelineType}`;
}



export function normalizeMetricsFromApi(apiMetrics = []) {

  return (apiMetrics || []).map((m) => ({

    name: m.name || "",

    namespace: m.namespace || "",

    statistics: m.statistics || [],

    dimensions: m.dimensions || {},

    onboarded: Boolean(m.onboarded),

    pipeline_name: m.pipeline_name,

    pipeline_type: m.pipeline_type,

    pipeline_region: m.pipeline_region,

  }));

}



/** Discovery snapshot onboarded flags can be stale; never trust them for scoped types. */
export function sanitizeDiscoveryMetricFlags(metrics = []) {
  return (metrics || []).map((m) => ({ ...m, onboarded: false }));
}


/** Prefer onboarded=true from API or discover snapshot; EMR uses pipeline scope only. */
export function mergeMetricsOnboardedFlags(apiMetrics = [], discoverMetrics = []) {
  const ACCOUNT_WIDE_TYPES = new Set([
    "Glue",
    "AWS/Glue",
    "AWS/StepFunctions",
    "AWS/ElasticMapReduce",
    "AWS/MWAA",
    "AmazonMWAA",
  ]);
  const isAccountWideMetric = (m) => {
    const ptype = (m?.pipeline_type || "").toLowerCase();
    const ns = m?.namespace || "";
    return (
      ptype === "glue"
      || ptype === "step_functions"
      || ptype === "step_function"
      || ptype === "emr"
      || ptype === "airflow"
      || ptype === "mwaa"
      || ACCOUNT_WIDE_TYPES.has(ns)
    );
  };

  const apiByPipelineKey = new Map(
    (apiMetrics || []).map((m) => [
      `${m.namespace}|${m.name}|${m.pipeline_name || ""}`,
      m,
    ]),
  );
  const apiByAccountKey = new Map(
    (apiMetrics || []).map((m) => [`${m.namespace}|${m.name}`, m]),
  );

  const isPipelineScopedMetric = (m) => {
    if (isAccountWideMetric(m)) {
      return false;
    }
    return false;
  };

  const base =
    (discoverMetrics || []).length > 0 ? discoverMetrics : apiMetrics || [];

  return base.map((m) => {
    const accountWide = isAccountWideMetric(m);
    const pipelineKey = `${m.namespace}|${m.name}|${m.pipeline_name || ""}`;
    const accountKey = `${m.namespace}|${m.name}`;
    const scoped = isPipelineScopedMetric(m);
    const api = accountWide
      ? apiByAccountKey.get(accountKey)
      : apiByPipelineKey.get(pipelineKey)
        || (!scoped ? apiByAccountKey.get(accountKey) : null);
    const apiDims = api?.dimensions && Object.keys(api.dimensions).length > 0
      ? api.dimensions
      : null;
    const discoverDims = m.dimensions && Object.keys(m.dimensions).length > 0
      ? m.dimensions
      : null;
    return {
      ...m,
      ...(api || {}),
      dimensions: apiDims || discoverDims || {},
      onboarded: scoped
        ? Boolean(api?.onboarded)
        : Boolean(api?.onboarded || m.onboarded),
    };
  });
}



export function pipelineSupportsMetricsPage(pipeline) {

  const t = (pipeline?.type || pipeline?.pipeline_type || "")

    .toLowerCase()

    .replace(/-/g, "_");

  if (
    ["glue", "step_functions", "step_function", "emr", "airflow", "mwaa"].includes(
      t,
    )
  ) {
    return true;
  }

  const pid = String(pipeline?.pipeline_id || pipeline?.id || "").toLowerCase();

  return (
    pid.startsWith("glue_") ||
    pid.startsWith("emr_") ||
    pid.startsWith("step_functions_") ||
    pid.startsWith("airflow_")
  );

}



/** One row per logical pipeline (EMR by cluster id; others by pipeline_id). */

export function dedupePipelinesByStableKey(pipelines = []) {

  const map = new Map();

  const score = (p) => {

    let s = 0;

    if (p.pipeline_id) s += 4;

    if (p.id) s += 2;

    const logs = [

      ...(p.log_paths?.cloudwatch || []),

      ...(p.log_paths?.s3 || []),

    ];

    if (logs.length > 0) s += 1;

    if (logs.some((item) => typeof item === "object" && item.onboarded)) s += 1;

    if ((p.metrics || []).length > 0) s += 2;

    return s;

  };

  for (const p of pipelines) {

    const key =

      (p.type === "emr" && p.id && `emr:${p.id}`) ||

      p.pipeline_id ||

      `${p.type || ""}:${p.name || ""}:${p.region || ""}`;

    const prev = map.get(key);

    if (!prev || score(p) > score(prev)) {

      map.set(key, p);

    }

  }

  return [...map.values()];

}



export function dedupePipelineGroups(groups = {}) {

  return {

    glue_jobs: dedupePipelinesByStableKey(groups.glue_jobs || []),

    step_functions: dedupePipelinesByStableKey(groups.step_functions || []),

    emr_clusters: dedupePipelinesByStableKey(groups.emr_clusters || []),

    airflow_dags: dedupePipelinesByStableKey(groups.airflow_dags || []),

  };

}



export function getPipelineLogDisplayCounts(pipeline) {

  const cw = pipeline?.log_paths?.cloudwatch || [];

  const s3 = pipeline?.log_paths?.s3 || [];

  const totalFromPaths = cw.length + s3.length;

  if (totalFromPaths > 0) {

    const onboarded =

      cw.filter((p) => typeof p === "object" && p.onboarded).length +

      s3.filter((p) => typeof p === "object" && p.onboarded).length;

    return { total: totalFromPaths, onboarded };

  }

  const onboarded = Number(pipeline?.logs_onboarded ?? 0);

  const total = Number(pipeline?.logs_total ?? onboarded);

  return { total: Math.max(total, onboarded), onboarded };

}



export function getPipelineMetricsDisplayCounts(pipeline, discoveryResults = null) {

  const metricItems = resolvePipelineMetrics(pipeline, discoveryResults);

  if (metricItems.length > 0) {

    return {

      total: metricItems.length,

      onboarded: metricItems.filter((metric) => Boolean(metric?.onboarded)).length,

    };

  }

  const onboarded = Number(pipeline?.metrics_onboarded ?? 0);

  const total = Number(pipeline?.metrics_total ?? 0);

  return {

    total: Math.max(total, onboarded),

    onboarded,

  };

}



export function getPipelineLogPathStrings(pipeline) {

  return [

    ...(pipeline?.log_paths?.cloudwatch || []),

    ...(pipeline?.log_paths?.s3 || []),

  ]

    .map((p) => (typeof p === "object" && p.path != null ? p.path : p))

    .filter(Boolean);

}



export const PIPELINE_SELECTION_TYPE_CONFIG = [
  { key: "glue", listKey: "glue_jobs" },
  { key: "stepfunctions", listKey: "step_functions" },
  { key: "emr", listKey: "emr_clusters" },
  { key: "airflow", listKey: "airflow_dags" },
];

export function getPipelineListForSelectionKey(pipelineGroups, selectionKey) {
  const config = PIPELINE_SELECTION_TYPE_CONFIG.find(
    (entry) => entry.key === selectionKey,
  );
  if (!config || !pipelineGroups) return [];
  return pipelineGroups[config.listKey] || [];
}

/** Resolve selected pipeline indices to the same list used by the pipeline cards. */
export function getSelectedPipelineRecords(selectedPipelines, pipelineGroups) {
  const records = [];

  if (!pipelineGroups) return records;

  for (const { key, listKey } of PIPELINE_SELECTION_TYPE_CONFIG) {
    const indices = selectedPipelines?.[key];
    const pipelineList = pipelineGroups[listKey] || [];
    if (!indices?.size) continue;

    indices.forEach((index) => {
      const pipeline = pipelineList[index];
      if (pipeline) {
        records.push({ pipeline, pipelineType: key });
      }
    });
  }

  return records;
}



function mergePathOnboardedStatus(pathMap, pathStr, onboarded) {
  if (!pathStr) return;
  pathMap.set(
    pathStr,
    pathMap.has(pathStr) ? pathMap.get(pathStr) || onboarded : onboarded,
  );
}

/**
 * Collect unique log paths for onboarding from selected pipelines.
 * All pipeline types (Glue, Step Functions, EMR, Airflow) share this path;
 * shared log groups are counted once per unique path.
 */
export function collectLogPathsForSelectedPipelines(
  selectedPipelines,
  pipelineGroups,
  discoveryResults,
) {
  const cloudwatchMap = new Map();
  const s3Map = new Map();
  let associationCount = 0;

  const globalOnboarded = new Map();
  for (const type of ["cloudwatch", "s3"]) {
    for (const item of discoveryResults?.log_paths?.[type] || []) {
      const pathStr = typeof item === "object" ? item.path : item;
      if (!pathStr) continue;
      if (typeof item === "object" && item.onboarded) {
        globalOnboarded.set(pathStr, true);
      }
    }
  }

  for (const { pipeline } of getSelectedPipelineRecords(
    selectedPipelines,
    pipelineGroups,
  )) {
    const logPaths = resolvePipelineLogPaths(pipeline, discoveryResults);

    for (const path of logPaths.cloudwatch || []) {
      const pathStr = typeof path === "object" ? path.path : path;
      if (!pathStr) continue;
      const onboarded =
        Boolean(typeof path === "object" && path.onboarded) ||
        Boolean(globalOnboarded.get(pathStr));
      mergePathOnboardedStatus(cloudwatchMap, pathStr, onboarded);
      associationCount += 1;
    }

    for (const path of logPaths.s3 || []) {
      const pathStr = typeof path === "object" ? path.path : path;
      if (!pathStr) continue;
      const onboarded =
        Boolean(typeof path === "object" && path.onboarded) ||
        Boolean(globalOnboarded.get(pathStr));
      mergePathOnboardedStatus(s3Map, pathStr, onboarded);
      associationCount += 1;
    }
  }

  const allEntries = [...cloudwatchMap.entries(), ...s3Map.entries()];
  const uniqueCount = allEntries.length;
  const alreadyOnboardedUniqueCount = allEntries.filter(([, onboarded]) =>
    onboarded,
  ).length;
  const newUniqueCount = uniqueCount - alreadyOnboardedUniqueCount;

  return {
    cloudwatch: new Set(cloudwatchMap.keys()),
    s3: new Set(s3Map.keys()),
    associationCount,
    uniqueCount,
    newUniqueCount,
    alreadyOnboardedUniqueCount,
  };
}



/** Count onboarded vs new rows in the Log Paths table (respects active filters). */
export function countLogPathTableStats(logPathRows) {
  const stats = { total: 0, onboarded: 0, new: 0 };
  for (const row of logPathRows || []) {
    stats.total += 1;
    if (row?.onboarded) stats.onboarded += 1;
    else stats.new += 1;
  }
  return stats;
}

/** Sum of per-pipeline log counts shown in the pipeline cards LOGS column. */
export function countSelectedPipelineLogAssociations(
  selectedPipelines,
  pipelineGroups,
  discoveryResults,
) {
  let total = 0;

  for (const { pipeline } of getSelectedPipelineRecords(
    selectedPipelines,
    pipelineGroups,
  )) {
    const logPaths = resolvePipelineLogPaths(pipeline, discoveryResults);
    total +=
      (logPaths.cloudwatch || []).length + (logPaths.s3 || []).length;
    if (
      (logPaths.cloudwatch || []).length + (logPaths.s3 || []).length === 0
    ) {
      total += getPipelineLogDisplayCounts(pipeline).total;
    }
  }

  return total;
}



export function getBestPipelineLogPathForFilter(pipeline) {

  const paths = getPipelineLogPathStrings(pipeline);

  const name = pipeline?.name || pipeline?.pipeline_name || "";

  if (!paths.length) return "";

  const named = paths.filter((p) => name && String(p).includes(name));

  if (named.length) {

    return named.sort((a, b) => String(b).length - String(a).length)[0];

  }

  return paths[0];

}



export function hasUnonboardedPipelineLogs(pipeline) {

  const { total, onboarded } = getPipelineLogDisplayCounts(pipeline);

  return total > 0 && onboarded < total;

}



export function getPipelineSyncCounts(pipeline, discoveryResults = null) {

  const logCounts = getPipelineLogDisplayCounts(pipeline);

  const metricCounts = getPipelineMetricsDisplayCounts(pipeline, discoveryResults);

  const remainingLogs = Math.max(0, logCounts.total - logCounts.onboarded);

  const remainingMetrics = Math.max(0, metricCounts.total - metricCounts.onboarded);

  return {

    totalLogs: logCounts.total,

    onboardedLogs: logCounts.onboarded,

    remainingLogs,

    totalMetrics: metricCounts.total,

    onboardedMetrics: metricCounts.onboarded,

    remainingMetrics,

    isFullySynced:

      (logCounts.total === 0 || remainingLogs === 0) &&

      (metricCounts.total === 0 || remainingMetrics === 0),

  };

}



export function matchPipelineRecord(pipeline, pipelineId) {

  if (!pipeline || !pipelineId) return false;

  return (

    pipeline.pipeline_id === pipelineId ||

    pipeline.id === pipelineId ||

    pipeline.name === pipelineId

  );

}



export function applyPipelineDiscoveryPatch(

  discoveryResults,

  pipelineId,

  patch = {},

) {

  if (!discoveryResults?.pipelines || !pipelineId) return discoveryResults;



  const groupKeys = [

    "glue_jobs",

    "step_functions",

    "emr_clusters",

    "airflow_dags",

  ];

  let foundKey = null;

  let foundIdx = -1;



  for (const key of groupKeys) {

    const list = discoveryResults.pipelines[key] || [];

    const idx = list.findIndex((p) => matchPipelineRecord(p, pipelineId));

    if (idx >= 0) {

      foundKey = key;

      foundIdx = idx;

      break;

    }

  }



  if (foundKey == null || foundIdx < 0) return discoveryResults;



  const existing = discoveryResults.pipelines[foundKey][foundIdx];

  const updatedPipeline = { ...existing, ...patch };



  if (patch.log_paths) {

    updatedPipeline.log_paths = patch.log_paths;

    const cw = patch.log_paths.cloudwatch || [];

    const s3 = patch.log_paths.s3 || [];

    updatedPipeline.logs_onboarded =

      cw.filter((p) => typeof p === "object" && p.onboarded).length +

      s3.filter((p) => typeof p === "object" && p.onboarded).length;

    updatedPipeline.logs_total = cw.length + s3.length;

  }



  if (patch.metrics) {

    updatedPipeline.metrics = patch.metrics;

  }



  const updatedList = [...discoveryResults.pipelines[foundKey]];

  updatedList[foundIdx] = updatedPipeline;



  return {

    ...discoveryResults,

    pipelines: {

      ...discoveryResults.pipelines,

      [foundKey]: updatedList,

    },

  };

}



export function buildPipelineLogsPageParams(pipeline, accountId) {

  const q = new URLSearchParams();

  if (accountId) {

    const id = String(accountId).startsWith("aws_")

      ? accountId

      : `aws_${accountId}`;

    q.set("accountId", id);

  }

  const pipelineName = pipeline?.name || pipeline?.pipeline_name;

  if (pipelineName) q.set("pipeline", pipelineName);

  // Strict mode: pass only pipeline-specific log path(s), not shared parent groups
  // (e.g. /aws-glue/jobs/output) that would match every job in the account.
  const paths = getPipelineLogPathStrings(pipeline);

  const nameLower = pipelineName ? String(pipelineName).toLowerCase() : "";

  const specific = nameLower

    ? paths.filter((p) => String(p).toLowerCase().includes(nameLower))

    : [];

  const toUse = specific.length ? specific : paths.slice(0, 1);

  if (toUse.length === 1) {

    q.set("logPath", toUse[0]);

  } else if (toUse.length > 1) {

    q.set("logPaths", toUse.map(encodeURIComponent).join("|"));

  }

  q.set("strict", "1");

  return q;

}



export function pipelineResourceId(pipeline) {

  if (!pipeline) return null;

  if (pipeline.type === "emr" && pipeline.id) return pipeline.id;

  const parsed = parsePipelineIdFields(pipeline.pipeline_id);

  if (pipeline.type === "emr" && parsed.emrClusterId) return parsed.emrClusterId;

  return pipeline.id || pipeline.pipeline_id || pipeline.name;

}



export const getAirflowEnvironmentName = (pipeline = {}) =>
  pipeline.environment_name ||
  pipeline.environmentName ||
  pipeline.workspace_name ||
  pipeline.workspaceName ||
  pipeline.environment ||
  null;



export const getAirflowDagId = (pipeline = {}) =>
  pipeline.dag_id || pipeline.id || pipeline.name || null;



export const isAirflowPipeline = (pipeline) =>
  String(pipeline?.type || "").toLowerCase() === "airflow" ||
  String(pipeline?.pipeline_id || "").startsWith("airflow_");



export const normalizeAirflowPipelineItem = (
  item = {},
  fallbackEnv = null,
  fallbackRegion = null,
  fallbackSource = null,
) => {
  const dagId = getAirflowDagId(item);
  const envName = getAirflowEnvironmentName(item) || fallbackEnv;
  const region = item.region || fallbackRegion || null;
  const logPath = item.log_path;

  return {
    ...item,
    name: item.name || dagId,
    dag_id: dagId,
    id: dagId,
    type: "airflow",
    region,
    environment_name: envName,
    workspace_name: envName,
    pipeline_id:
      item.pipeline_id ||
      (dagId && region ? `airflow_${dagId}_${region}` : undefined),
    log_paths: item.log_paths || {
      cloudwatch: logPath ? [logPath] : [],
      s3: [],
    },
    metrics: item.metrics || [],
    status:
      item.status ||
      (item.is_active === false ? "INACTIVE" : "ACTIVE"),
    is_active: item.is_active !== false,
    is_paused: Boolean(item.is_paused),
    status_source: item.status_source || item.discovery_source || fallbackSource || null,
  };
};


export const MWAA_METRIC_NAMESPACE_OPTIONS = [
  { value: "all", label: "All namespaces" },
  { value: "AmazonMWAA", label: "AmazonMWAA" },
  { value: "AWS/MWAA", label: "AWS/MWAA" },
];

export const mwaaNamespaceLabel = (namespace = "") => {
  return namespace || "Unknown namespace";
};

export function filterMetricsByMwaaNamespace(metrics = [], filter = "all") {
  if (!filter || filter === "all") return metrics || [];
  return (metrics || []).filter((m) => m.namespace === filter);
}

export function metricsOnboardSummaryByNamespace(metrics = []) {
  const groups = {};
  for (const m of metrics || []) {
    const ns = m.namespace || "unknown";
    if (!groups[ns]) {
      groups[ns] = { total: 0, onboarded: 0 };
    }
    groups[ns].total += 1;
    if (m.onboarded) groups[ns].onboarded += 1;
  }
  return groups;
}

export function groupMetricsByNamespace(metrics = []) {
  const order = ["AmazonMWAA", "AWS/MWAA"];
  const groups = new Map();
  for (const m of metrics || []) {
    const ns = m.namespace || "unknown";
    if (!groups.has(ns)) groups.set(ns, []);
    groups.get(ns).push(m);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    const ai = order.indexOf(a);
    const bi = order.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

